import type { CreateLicense } from '@wf-coe/types';
import {
  Button,
  Checkbox,
  HelperText,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Radio,
  Select,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
  TextInput,
  ToggleSwitch,
} from 'flowbite-react';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { FiCopy, FiEdit2, FiKey, FiPlay, FiPlus, FiTrash2 } from 'react-icons/fi';
import { useParams } from 'react-router-dom';
import { TriggerActionModal } from '../../components/clients/TriggerActionModal.js';
import { useCreateLicenseMutation } from '../../queries/use-create-license.js';
import { useDeleteLicenseMutation } from '../../queries/use-delete-license.js';
import { useFusionConfigsQuery } from '../../queries/use-fusion-configs.js';
import {
  useFusionOrgsQuery,
  useFusionScenarioFoldersByTeamsQuery,
  useFusionScenariosByTeamsQuery,
  type FoldersByTeam,
  type FusionOrg,
  type FusionScenario,
  type FusionScenarioFolder,
  type ScenariosByTeam,
} from '../../queries/use-fusion-picker.js';
import {
  useWorkfrontPortfoliosQuery,
  useWorkfrontProgramsQuery,
  useWorkfrontTemplatesQuery,
} from '../../queries/use-workfront-picker.js';
import { type LicenseDto, useLicenseQuery, useLicensesQuery } from '../../queries/use-licenses.js';
import { useRotateEngineCredentialsMutation } from '../../queries/use-rotate-engine-credentials.js';

import { useUpdateLicenseMutation } from '../../queries/use-update-license.js';
import { useWorkfrontConfigsQuery } from '../../queries/use-workfront-configs.js';

/**
 * `/clients/{id}/licenses` — manages a tenant's audit / backup license
 * rows. The edit modal is a 4-step wizard (Basic → Fusion → Workfront →
 * Flags) matching the legacy admin-console UX. Workfront + Fusion
 * config pickers populate from the same hooks the other tabs use.
 */
export function Licenses(): JSX.Element {
  const { id: clientId } = useParams<{ id: string }>();
  const { data, error, isLoading, isError } = useLicensesQuery(clientId);
  const workfrontQuery = useWorkfrontConfigsQuery(clientId);
  const fusionQuery = useFusionConfigsQuery(clientId);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<LicenseDto | null>(null);
  const [deleting, setDeleting] = useState<LicenseDto | null>(null);
  // Rotation flow (ADR 0012). Two stages so we never show the secret
  // without an explicit operator confirmation:
  //   - `rotating`: a license queued for rotation, awaiting confirm.
  //   - `revealed`: { engineApiClientId, engineApiClientSecret } from
  //     a successful rotation OR a freshly-created license. The secret
  //     is only in memory until the disclosure modal is dismissed.
  const [rotating, setRotating] = useState<LicenseDto | null>(null);
  const [revealed, setRevealed] = useState<RevealedSecret | null>(null);
  const [triggering, setTriggering] = useState<LicenseDto | null>(null);

  const workfrontConfigs = workfrontQuery.data ?? [];
  const fusionConfigs = fusionQuery.data ?? [];

  const handleNew = (): void => {
    setEditing(null);
    setEditorOpen(true);
  };
  const handleEdit = (license: LicenseDto): void => {
    setEditing(license);
    setEditorOpen(true);
  };

  const workfrontNameById = new Map(workfrontConfigs.map((c) => [c.id, c.name]));
  const fusionNameById = new Map(fusionConfigs.map((c) => [c.id, c.name]));

  return (
    <section>
      <header className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Licenses</h2>
        <Button size="sm" onClick={handleNew} disabled={!clientId}>
          <FiPlus className="mr-1" /> New license
        </Button>
      </header>

      {isLoading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400" role="status">
          Loading licenses…
        </p>
      ) : isError ? (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
        >
          {error instanceof Error ? error.message : 'Failed to load licenses'}
        </div>
      ) : data && data.length === 0 ? (
        <EmptyState onCreate={handleNew} />
      ) : data ? (
        <LicensesTable
          licenses={data}
          clientId={clientId}
          workfrontNameById={workfrontNameById}
          fusionNameById={fusionNameById}
          onEdit={handleEdit}
          onDelete={setDeleting}
          onRotate={setRotating}
          onTrigger={setTriggering}
        />
      ) : null}

      {clientId ? (
        <LicenseEditModal
          open={editorOpen}
          onClose={() => setEditorOpen(false)}
          clientId={clientId}
          existing={editing}
          workfrontConfigs={workfrontConfigs}
          fusionConfigs={fusionConfigs}
          onCreatedDisclosure={setRevealed}
        />
      ) : null}
      {clientId && deleting ? (
        <LicenseDeleteModal
          open={Boolean(deleting)}
          onClose={() => setDeleting(null)}
          clientId={clientId}
          license={deleting}
        />
      ) : null}
      {clientId && rotating ? (
        <LicenseRotateConfirmModal
          open={Boolean(rotating)}
          onClose={() => setRotating(null)}
          clientId={clientId}
          license={rotating}
          onRotated={(payload) => {
            setRotating(null);
            setRevealed(payload);
          }}
        />
      ) : null}
      {revealed ? (
        <EngineApiSecretDisclosureModal
          open={Boolean(revealed)}
          onClose={() => setRevealed(null)}
          revealed={revealed}
        />
      ) : null}
      {triggering ? (
        <TriggerActionModal
          isOpen={Boolean(triggering)}
          onClose={() => setTriggering(null)}
          license={triggering}
        />
      ) : null}
    </section>
  );
}

interface RevealedSecret {
  engineApiClientId: string;
  engineApiClientSecret: string;
  /** "created" or "rotated" — affects the modal copy. */
  origin: 'created' | 'rotated';
}

function EmptyState({ onCreate }: { onCreate: () => void }): JSX.Element {
  return (
    <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center dark:border-gray-600 dark:bg-gray-800">
      <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">No licenses yet</h3>
      <p className="mx-auto mt-1 max-w-md text-sm text-gray-500 dark:text-gray-400">
        Add a license row pairing a Workfront connection with a Fusion connection so audit + backup
        scenarios know what to run.
      </p>
      <Button size="sm" className="mx-auto mt-4" onClick={onCreate}>
        <FiPlus className="mr-1" /> New license
      </Button>
    </div>
  );
}

function LicensesTable({
  licenses,
  clientId,
  workfrontNameById,
  fusionNameById,
  onEdit,
  onDelete,
  onRotate,
  onTrigger,
}: {
  licenses: LicenseDto[];
  clientId: string | undefined;
  workfrontNameById: Map<string, string>;
  fusionNameById: Map<string, string>;
  onEdit: (license: LicenseDto) => void;
  onDelete: (license: LicenseDto) => void;
  onRotate: (license: LicenseDto) => void;
  onTrigger: (license: LicenseDto) => void;
}): JSX.Element {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
      <Table>
        <TableHead>
          <TableRow>
            <TableHeadCell>Type</TableHeadCell>
            <TableHeadCell>Legacy ID</TableHeadCell>
            <TableHeadCell>Primary</TableHeadCell>
            <TableHeadCell>Enabled</TableHeadCell>
            <TableHeadCell>Workfront</TableHeadCell>
            <TableHeadCell>Fusion</TableHeadCell>
            <TableHeadCell>Engine API</TableHeadCell>
            <TableHeadCell>
              <span className="sr-only">Actions</span>
            </TableHeadCell>
          </TableRow>
        </TableHead>
        <TableBody className="divide-y divide-gray-200 dark:divide-gray-700">
          {licenses.map((license) => (
            <TableRow key={license.id} className="bg-white dark:bg-gray-800">
              <TableCell className="font-medium text-gray-900 dark:text-gray-100">
                {LICENSE_TYPE_LABEL[license.type] ?? license.type}
              </TableCell>
              <TableCell>
                {/*
                 * Back-reference to the legacy `wf-audit-engine`
                 * `licenses.id` (BIGINT) row this license was cloned
                 * from. Populated by `scripts/migrate-legacy-to-coe-dev/`
                 * and by the `backfill-legacy-license-ids` Lambda;
                 * `null` for natively-created rows (the seeded dev
                 * license + anything created post-cutover). Useful
                 * for operators cross-referencing tickets / logs
                 * against the legacy stack during the parallel-stack
                 * window.
                 */}
                {license.legacyLicenseId ? (
                  <span className="font-mono text-xs text-gray-700 dark:text-gray-300">
                    {license.legacyLicenseId}
                  </span>
                ) : (
                  <span className="text-gray-400 dark:text-gray-600">—</span>
                )}
              </TableCell>
              <TableCell>{license.isPrimary ? 'Yes' : '—'}</TableCell>
              <TableCell>{license.enabled ? 'Yes' : '—'}</TableCell>
              <TableCell>
                {license.workfrontConfigId
                  ? (workfrontNameById.get(license.workfrontConfigId) ?? '—')
                  : '—'}
              </TableCell>
              <TableCell>
                {license.fusionConfigId ? (fusionNameById.get(license.fusionConfigId) ?? '—') : '—'}
              </TableCell>
              <TableCell>
                <EngineApiClientIdCell engineApiClientId={license.engineApiClientId} />
              </TableCell>
              <TableCell>
                <div className="flex items-center justify-end gap-2">
                  {/*
                   * No per-license SecretsRowButton anymore — after the
                   * 2026-05-12 / 2026-05-13 consolidations, every
                   * engine-read credential lives on the per-Fusion-
                   * connection bundle. The engine API client_secret
                   * still rotates via the key icon below; that's a
                   * dedicated server-side flow, not a bundle field.
                   */}
                  {license.engineApiClientId ? (
                    <button
                      type="button"
                      onClick={() => onRotate(license)}
                      aria-label={`Rotate engine credentials for ${license.type} license`}
                      title="Rotate engine API credential"
                      className="rounded p-1.5 text-amber-600 hover:bg-amber-50 hover:text-amber-700 dark:text-amber-400 dark:hover:bg-amber-950 dark:hover:text-amber-300"
                    >
                      <FiKey size={16} />
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => onTrigger(license)}
                    aria-label={`Trigger system action for ${license.type} license`}
                    title="Trigger system action"
                    className="rounded p-1.5 text-blue-600 hover:bg-blue-50 hover:text-blue-800 dark:text-blue-400 dark:hover:bg-blue-950 dark:hover:text-blue-300"
                  >
                    <FiPlay size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => onEdit(license)}
                    aria-label={`Edit ${license.type} license`}
                    className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                  >
                    <FiEdit2 size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(license)}
                    aria-label={`Delete ${license.type} license`}
                    className="rounded p-1.5 text-red-500 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-950 dark:hover:text-red-300"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * Renders the public half of the engine-API credential pair. UUID is
 * truncated for visual consistency with the other ID columns; the full
 * value is exposed in the `title` attribute so operators copying for
 * onboarding can hover-and-grab it.
 */
function EngineApiClientIdCell({
  engineApiClientId,
}: {
  engineApiClientId: string | null;
}): JSX.Element {
  if (!engineApiClientId) {
    return <span className="text-gray-400 dark:text-gray-500">—</span>;
  }
  return (
    <code title={engineApiClientId} className="font-mono text-xs text-gray-700 dark:text-gray-300">
      {engineApiClientId.slice(0, 8)}…
    </code>
  );
}

/**
 * Tri-state sync-scope choice gating the scenario-folder / scenario
 * multi-selects on the Fusion step. Mutually exclusive — picking
 * `'folder'` clears `fusionScenarioIds`, picking `'scenario'` clears
 * `fusionScenarioFolderIds`, picking `'none'` clears both.
 *
 * - `'none'`   (default): engine syncs every scenario in the selected
 *              teams. The common case.
 * - `'folder'`: engine syncs every scenario inside the chosen folder(s).
 * - `'scenario'`: engine syncs only the explicitly-checked scenarios.
 *
 * Not persisted. Derived on edit by inspecting which of the two arrays
 * is non-empty (scenarios win over folders when both are non-empty,
 * which shouldn't happen but is handled defensively).
 */
type NarrowMode = 'none' | 'folder' | 'scenario';

interface FormState {
  type: string;
  isPrimary: boolean;
  enabled: boolean;
  fusionConfigId: string;
  /** Auto-filled from the picked org option. Stored for engine compat. */
  fusionOrgName: string;
  fusionOrgId: string;
  /** Auto-filled from the first picked team. */
  fusionTeamName: string;
  /** Multi-select team ids (BIGINT-as-string). */
  fusionTeamIds: string[];
  /** Tri-state narrow-scope radio. See `NarrowMode`. */
  narrowMode: NarrowMode;
  /** Multi-select scenario folder ids (BIGINT-as-string). */
  fusionScenarioFolderIds: string[];
  /** Auto-filled from the first picked scenario. */
  fusionScenarioName: string;
  /** Multi-select scenario ids (BIGINT-as-string). */
  fusionScenarioIds: string[];
  workfrontConfigId: string;
  workfrontObjectDetectionConfigId: string;
  /**
   * UUIDs of secondary workfront-configs linked to this license via
   * `license_secondary_workfront_configs`. Used by the customer-app
   * custom-forms migration wizard (clone-across-workspaces).
   */
  secondaryWorkfrontConfigIds: string[];
  workfrontPortfolioId: string;
  workfrontProgramId: string;
  workfrontTemplateId: string;
  workfrontReuseBackupConfig: boolean;
  aiSummaryEnabled: boolean;
  logMetricsEnabled: boolean;
  githubBackupEnabled: boolean;
  workfrontBackupEnabled: boolean;
  /** Selected KPI metric id as the `<select>` value; `''` = none. Sent as a number. */
  mainMetricId: string;
  /**
   * Read-only carry-through. The operator UI doesn't expose this — the
   * only writers are `scripts/migrate-legacy-to-coe-dev/` (sets it on
   * clone) and the `backfill-legacy-license-ids` Lambda. But the wizard
   * still has to round-trip the value: `toCreateBody` is shared between
   * Create + Edit, so previously hardcoding `null` here nuked the
   * column on every edit (bug fix 2026-05-18).
   */
  legacyLicenseId: string | null;
}

const LICENSE_TYPES: { value: string; label: string }[] = [
  { value: 'pro', label: 'Professional' },
  { value: 'enterprise', label: 'Enterprise' },
];

const LICENSE_TYPE_LABEL: Record<string, string> = Object.fromEntries(
  LICENSE_TYPES.map((t) => [t.value, t.label]),
);

const STEPS = ['Basic', 'Fusion', 'Workfront', 'Flags'] as const;

function emptyState(): FormState {
  return {
    type: 'pro',
    isPrimary: false,
    enabled: false,
    fusionConfigId: '',
    fusionOrgName: '',
    fusionOrgId: '',
    fusionTeamName: '',
    fusionTeamIds: [],
    narrowMode: 'none',
    fusionScenarioFolderIds: [],
    fusionScenarioName: '',
    fusionScenarioIds: [],
    workfrontConfigId: '',
    workfrontObjectDetectionConfigId: '',
    secondaryWorkfrontConfigIds: [],
    workfrontPortfolioId: '',
    workfrontProgramId: '',
    workfrontTemplateId: '',
    workfrontReuseBackupConfig: true,
    aiSummaryEnabled: false,
    logMetricsEnabled: false,
    githubBackupEnabled: false,
    workfrontBackupEnabled: false,
    mainMetricId: '',
    legacyLicenseId: null,
  };
}

function fromExisting(existing: LicenseDto): FormState {
  // Pre-fill straight from the multi columns. The 20260511 migration
  // backfilled them from the now-dropped single columns; rows created
  // after that point write only the arrays.
  const scenarioFolderIds = existing.fusionScenarioFolderIds;
  const scenarioIds = existing.fusionScenarioIds;
  return {
    type: existing.type,
    isPrimary: existing.isPrimary,
    enabled: existing.enabled,
    fusionConfigId: existing.fusionConfigId ?? '',
    fusionOrgName: existing.fusionOrgName ?? '',
    fusionOrgId: existing.fusionOrgId ?? '',
    fusionTeamName: existing.fusionTeamName ?? '',
    fusionTeamIds: existing.fusionTeamIds,
    // Derive the radio choice from whichever array carries selections.
    // Scenarios win over folders if (defensively) both are non-empty —
    // an explicit scenario list is the more specific intent.
    narrowMode:
      scenarioIds.length > 0 ? 'scenario' : scenarioFolderIds.length > 0 ? 'folder' : 'none',
    fusionScenarioFolderIds: scenarioFolderIds,
    fusionScenarioName: existing.fusionScenarioName ?? '',
    fusionScenarioIds: scenarioIds,
    workfrontConfigId: existing.workfrontConfigId ?? '',
    workfrontObjectDetectionConfigId: existing.workfrontObjectDetectionConfigId ?? '',
    secondaryWorkfrontConfigIds: (existing.secondaryWorkfrontConfigs ?? []).map((s) => s.id),
    workfrontPortfolioId: existing.workfrontPortfolioId,
    workfrontProgramId: existing.workfrontProgramId,
    workfrontTemplateId: existing.workfrontTemplateId ?? '',
    workfrontReuseBackupConfig: existing.workfrontReuseBackupConfig,
    aiSummaryEnabled: existing.aiSummaryEnabled,
    logMetricsEnabled: existing.logMetricsEnabled,
    githubBackupEnabled: existing.githubBackupEnabled,
    workfrontBackupEnabled: existing.workfrontBackupEnabled,
    mainMetricId: existing.mainMetricId == null ? '' : String(existing.mainMetricId),
    legacyLicenseId: existing.legacyLicenseId,
  };
}

const BIGINT_RE = /^-?\d+$/;

function nullIfBlank(s: string): string | null {
  return s.trim() === '' ? null : s.trim();
}

function bigIntOrNull(s: string): string | null {
  const t = s.trim();
  return t === '' ? null : t;
}

function validateBigInt(label: string, value: string): string | null {
  const t = value.trim();
  if (t === '') return null;
  return BIGINT_RE.test(t) ? null : `${label} must be a numeric id`;
}

function validateStep(state: FormState, step: number): string | null {
  if (step === 0) {
    if (state.type.trim() === '') return 'Type is required';
    return null;
  }
  if (step === 1) {
    // The picker UI guarantees numeric ids — every value in these
    // arrays came from a Fusion `{id, name}` pair. Belt-and-suspenders
    // check in case state was mutated outside the picker.
    const fusionOrgIdErr = validateBigInt('Fusion org id', state.fusionOrgId);
    if (fusionOrgIdErr) return fusionOrgIdErr;
    for (const id of state.fusionTeamIds) {
      if (!BIGINT_RE.test(id)) return `Fusion team ids: "${id}" is not a numeric id`;
    }
    for (const id of state.fusionScenarioFolderIds) {
      if (!BIGINT_RE.test(id)) return `Fusion scenario folder ids: "${id}" is not a numeric id`;
    }
    for (const id of state.fusionScenarioIds) {
      if (!BIGINT_RE.test(id)) return `Fusion scenario ids: "${id}" is not a numeric id`;
    }
    return null;
  }
  if (step === 2) {
    if (state.workfrontPortfolioId.trim() === '') return 'Workfront portfolio id is required';
    if (state.workfrontProgramId.trim() === '') return 'Workfront program id is required';
    return null;
  }
  return null;
}

function toCreateBody(state: FormState): CreateLicense {
  return {
    type: state.type.trim(),
    isPrimary: state.isPrimary,
    enabled: state.enabled,
    fusionConfigId: nullIfBlank(state.fusionConfigId),
    fusionOrgName: nullIfBlank(state.fusionOrgName),
    fusionOrgId: bigIntOrNull(state.fusionOrgId),
    fusionTeamName: nullIfBlank(state.fusionTeamName),
    // Legacy single team-id column is null on the wire; repository
    // mirrors `fusionTeamIds[0]` for engine compat. The other legacy
    // single columns (`fusion_scenario_folder_id`, `fusion_scenario_id`)
    // were dropped 2026-05-13 since no engine consumer reads them.
    fusionTeamId: null,
    fusionTeamIds: state.fusionTeamIds,
    fusionFolderId: null,
    fusionScenarioFolderIds: state.fusionScenarioFolderIds,
    fusionScenarioName: nullIfBlank(state.fusionScenarioName),
    fusionScenarioIds: state.fusionScenarioIds,
    workfrontConfigId: nullIfBlank(state.workfrontConfigId),
    workfrontObjectDetectionConfigId: nullIfBlank(state.workfrontObjectDetectionConfigId),
    // Strip the primary out of the secondary set if the operator
    // accidentally checked both — DB FK is fine with it but the wizard
    // intent is "additional connections beyond primary".
    secondaryWorkfrontConfigIds: state.secondaryWorkfrontConfigIds.filter(
      (id) => id && id !== state.workfrontConfigId,
    ),
    workfrontPortfolioId: state.workfrontPortfolioId.trim(),
    workfrontProgramId: state.workfrontProgramId.trim(),
    workfrontTemplateId: nullIfBlank(state.workfrontTemplateId),
    workfrontReuseBackupConfig: state.workfrontReuseBackupConfig,
    aiSummaryEnabled: state.aiSummaryEnabled,
    logMetricsEnabled: state.logMetricsEnabled,
    githubBackupEnabled: state.githubBackupEnabled,
    workfrontBackupEnabled: state.workfrontBackupEnabled,
    mainMetricId: state.mainMetricId === '' ? null : Number(state.mainMetricId),
    // legacy_license_id is never edited via the operator UI — only the
    // legacy-clone script + the backfill Lambda set it on the create
    // path. But we MUST round-trip the existing value on edit: the
    // repository's update path treats `legacyLicenseId !== undefined`
    // as "overwrite", so hardcoding `null` here used to wipe the
    // column on every save. The wizard now reads from state, which
    // `fromExisting` preloads with the existing license's value (or
    // `null` for new licenses).
    legacyLicenseId: state.legacyLicenseId,
  };
}

function LicenseEditModal({
  open,
  onClose,
  clientId,
  existing,
  workfrontConfigs,
  fusionConfigs,
  onCreatedDisclosure,
}: {
  open: boolean;
  onClose: () => void;
  clientId: string;
  existing: LicenseDto | null;
  workfrontConfigs: { id: string; name: string }[];
  fusionConfigs: { id: string; name: string }[];
  onCreatedDisclosure: (revealed: RevealedSecret) => void;
}): JSX.Element {
  const create = useCreateLicenseMutation();
  const update = useUpdateLicenseMutation();
  // The list row has no KPI metrics; the main-metric picker reads them from the
  // license detail. A new license has no detail (and no metrics) yet.
  const detail = useLicenseQuery(open ? clientId : undefined, open ? existing?.id : undefined);
  const metricOptions = detail.data?.kpiMetricOptions ?? [];
  const metricsEmptyLabel = !existing
    ? 'No KPI metrics yet'
    : detail.isPending
      ? 'Loading KPI metrics…'
      : detail.isError
        ? 'Could not load KPI metrics'
        : 'No KPI metrics yet';
  const [state, setState] = useState<FormState>(emptyState);
  const [step, setStep] = useState(0);
  const [validationError, setValidationError] = useState<string | null>(null);
  // True once the operator has picked a main metric themselves; until then the
  // field follows the license detail (fresher than the list row it was seeded from).
  const mainMetricTouched = useRef(false);

  useEffect(() => {
    if (open) {
      mainMetricTouched.current = false;
      setState(existing ? fromExisting(existing) : emptyState());
      setStep(0);
      setValidationError(null);
      create.reset();
      update.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existing?.id]);

  // The list row can be stale (e.g. the main metric was deleted since it was
  // fetched), which would leave a hidden id the picker cannot show or clear.
  // Declared after the effect above so, on open, it overrides the row-seeded value.
  const detailMainMetricId = detail.data?.mainMetricId;
  useEffect(() => {
    if (!open || !existing || mainMetricTouched.current || detailMainMetricId === undefined) {
      return;
    }
    const seeded = detailMainMetricId == null ? '' : String(detailMainMetricId);
    setState((s) => (s.mainMetricId === seeded ? s : { ...s, mainMetricId: seeded }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existing?.id, detailMainMetricId]);

  const isPending = create.isPending || update.isPending;
  const error = create.error ?? update.error;
  const set = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
    if (key === 'mainMetricId') mainMetricTouched.current = true;
    setState((s) => ({ ...s, [key]: value }));
  };

  const handleNext = (): void => {
    const err = validateStep(state, step);
    if (err) {
      setValidationError(err);
      return;
    }
    setValidationError(null);
    setStep(step + 1);
  };

  const handlePrev = (): void => {
    setValidationError(null);
    setStep(step - 1);
  };

  const handleSave = (): void => {
    for (let s = 0; s <= 3; s += 1) {
      const err = validateStep(state, s);
      if (err) {
        setValidationError(err);
        setStep(s);
        return;
      }
    }
    setValidationError(null);
    const body = toCreateBody(state);
    if (existing) {
      update.mutate({ clientId, licenseId: existing.id, patch: body }, { onSuccess: onClose });
    } else {
      create.mutate(
        { clientId, body },
        {
          onSuccess: (created) => {
            onClose();
            // ADR 0012 — auto-mint exposes the engineApiClientSecret
            // on the 201 response (only here, never on subsequent
            // reads). Surface it via the disclosure modal so the
            // operator can copy + paste it to wf-audit's
            // license-detail store. The shape is duck-typed because
            // the SPA's LicenseDto doesn't carry the secret in its
            // standard form.
            const c = created as LicenseDto & { engineApiClientSecret?: string };
            if (c.engineApiClientId && c.engineApiClientSecret) {
              onCreatedDisclosure({
                engineApiClientId: c.engineApiClientId,
                engineApiClientSecret: c.engineApiClientSecret,
                origin: 'created',
              });
            }
          },
        },
      );
    }
  };

  const isLastStep = step === STEPS.length - 1;

  return (
    <Modal show={open} onClose={onClose} dismissible size="2xl">
      <ModalHeader>{existing ? 'Edit license' : 'Add new license'}</ModalHeader>
      {/*
        Plain `<form>` with `onSubmit={preventDefault}` — implicit
        submission is intentionally a no-op so Enter in a TextInput on
        any wizard step doesn't fire the mutation. Save is reachable
        only via the explicit type="button" + onClick path. Without
        this, on the last step (where all earlier-step validations are
        already satisfied because fromExisting prefilled the fields),
        any Enter keypress on any input flushed handleSubmit and
        onSuccess closed the modal mid-review.
      */}
      <form
        onSubmit={(e: FormEvent<HTMLFormElement>) => e.preventDefault()}
        aria-label="License form"
      >
        {/*
          `max-h-[calc(100vh-12rem)] overflow-y-auto` on ModalBody —
          Flowbite-React's default Modal lets the body grow to its
          natural height, which on the Fusion step (long radio + a
          team-grouped picker tall enough to break out of a 1080p
          viewport) bleeds past the modal's box, breaking the
          backdrop and making the *page* scroll instead of the body.
          Constraining the body to viewport-minus-header-footer +
          internal overflow keeps the picker self-contained.

          The scrollbar utilities cover both engines:
            - WebKit (`::-webkit-scrollbar*`) for Chrome / Safari /
              Edge — gives us full track + thumb control.
            - `scrollbar-color` for Firefox + Chrome 121+ — same
              colors as the WebKit thumb/track.
          Both pairs include dark-mode variants so the bar matches
          the gray-800 modal surface instead of falling back to the
          OS's default light scrollbar.
        */}
        <ModalBody className="max-h-[calc(100vh-12rem)] overflow-y-auto bg-white [scrollbar-color:#d1d5db_transparent] [&::-webkit-scrollbar]:w-2.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb:hover]:bg-gray-400 [&::-webkit-scrollbar-track]:bg-transparent dark:bg-gray-800 dark:[scrollbar-color:#4b5563_transparent] dark:[&::-webkit-scrollbar-thumb]:bg-gray-600 dark:[&::-webkit-scrollbar-thumb:hover]:bg-gray-500">
          <Stepper step={step} />
          <hr className="my-4 border-gray-200 dark:border-gray-700" />
          <div className="space-y-4">
            {step === 0 ? (
              <BasicStep state={state} set={set} disabled={isPending} />
            ) : step === 1 ? (
              <FusionStep
                state={state}
                set={set}
                fusionConfigs={fusionConfigs}
                clientId={clientId}
                disabled={isPending}
              />
            ) : step === 2 ? (
              <WorkfrontStep
                state={state}
                set={set}
                workfrontConfigs={workfrontConfigs}
                clientId={clientId}
                disabled={isPending}
              />
            ) : (
              <FlagsStep
                state={state}
                set={set}
                disabled={isPending}
                metricOptions={metricOptions}
                metricsEmptyLabel={metricsEmptyLabel}
              />
            )}

            {validationError ? (
              <div
                role="alert"
                className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200"
              >
                {validationError}
              </div>
            ) : null}
            {error ? (
              <div
                role="alert"
                className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
              >
                {error instanceof Error ? error.message : 'Save failed'}
              </div>
            ) : null}
          </div>
        </ModalBody>
        {/*
          Force the footer's surface to match ModalBody. Flowbite's
          default ModalFooter theme paints `bg-gray-50 dark:bg-gray-700`
          (one shade lighter than the body's `bg-gray-800` in dark
          mode) — the seam is invisible while the body is short, but
          stark once the body becomes a scrolling region. Pinning both
          surfaces to the same shade removes the dual-tone strip
          behind the action row.
        */}
        <ModalFooter className="justify-between border-t border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            <span className="text-red-500">*</span> required
          </span>
          <div className="flex gap-2">
            <Button type="button" color="gray" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            {step > 0 ? (
              <Button type="button" color="gray" onClick={handlePrev} disabled={isPending}>
                Previous
              </Button>
            ) : null}
            {!isLastStep ? (
              <Button type="button" onClick={handleNext} disabled={isPending}>
                Next
              </Button>
            ) : (
              <Button type="button" onClick={handleSave} disabled={isPending}>
                {isPending ? 'Saving…' : existing ? 'Save' : 'Submit'}
              </Button>
            )}
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
}

function Stepper({ step }: { step: number }): JSX.Element {
  return (
    <ol
      aria-label="License wizard steps"
      className="flex w-full items-center px-2 text-center text-xs font-medium text-gray-500 sm:text-sm dark:text-gray-400"
    >
      {STEPS.map((label, idx) => {
        const isActive = idx === step;
        const isDone = idx < step;
        const isLast = idx === STEPS.length - 1;
        const circleClasses = isActive
          ? 'border-orange-500 text-orange-500'
          : isDone
            ? 'border-green-500 bg-green-500 text-white'
            : 'border-gray-300 text-gray-400 dark:border-gray-600';
        const lineClasses = isDone
          ? 'after:border-green-500'
          : 'after:border-gray-300 dark:after:border-gray-600';
        return (
          <li
            key={label}
            className={
              isLast
                ? 'flex items-center'
                : `flex w-full items-center after:inline-block after:h-px after:w-full after:border-b after:content-[''] ${lineClasses}`
            }
          >
            <div className="relative flex flex-col items-center">
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full border font-semibold ${circleClasses}`}
                aria-current={isActive ? 'step' : undefined}
              >
                {idx + 1}
              </div>
              <span
                className={`absolute mt-9 whitespace-nowrap ${isActive ? 'font-bold text-gray-700 dark:text-gray-200' : ''}`}
              >
                {label}
              </span>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function BasicStep({
  state,
  set,
  disabled,
}: {
  state: FormState;
  set: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
  disabled: boolean;
}): JSX.Element {
  return (
    <div className="mt-8 space-y-4">
      <div>
        <Label htmlFor="lic-type">
          Type <span className="text-red-500">*</span>
        </Label>
        <Select
          id="lic-type"
          value={state.type}
          onChange={(e) => set('type', e.target.value)}
          disabled={disabled}
        >
          {LICENSE_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </Select>
      </div>
      <ToggleRow
        id="lic-primary"
        label="Is primary"
        checked={state.isPrimary}
        onChange={(c) => set('isPrimary', c)}
        disabled={disabled}
      />
      <ToggleRow
        id="lic-enabled"
        label="Enabled"
        checked={state.enabled}
        onChange={(c) => set('enabled', c)}
        disabled={disabled}
      />
    </div>
  );
}

/**
 * Cascading Fusion picker. Drives:
 *   1. Fusion connection dropdown (existing fusion_configs rows for
 *      the tenant).
 *   2. Org dropdown — driven by the connection's apiKey via the
 *      `useFusionOrgsQuery` proxy. The org payload carries nested
 *      teams, so #3 doesn't need a separate fetch.
 *   3. Teams checkbox panel (multi-select) — filtered to the chosen
 *      org's nested team list.
 *   4. Sync-scope tri-state radio — `none` (default) / `folder` /
 *      `scenario`. Picks the narrowing mode and gates which of the
 *      next two panels renders.
 *   5a. Scenario folders checkbox panel (`narrowMode === 'folder'`,
 *       multi-select) — driven by the first selected team via
 *       `useFusionScenarioFoldersQuery`.
 *   5b. Scenarios grouped-checkbox panel (`narrowMode === 'scenario'`,
 *       multi-select) — driven by the first selected team via
 *       `useFusionScenariosQuery`, grouped by parent folder so
 *       operators can scan tenants with hundreds of scenarios.
 *
 * Auto-fill on selection: `fusionOrgName` from the chosen org option,
 * `fusionTeamName` from the first picked team, `fusionScenarioName`
 * from the first picked scenario. The engine's legacy single-id
 * columns are populated server-side from `array[0]`; the wizard only
 * writes the arrays.
 *
 * Dependent fetches are gated behind their parent selection — the
 * teams panel is dim until an org is picked, the scenarios panel is
 * dim until at least one team is picked, etc. TanStack's `enabled`
 * flag handles the gating; the SPA never sends an upstream Fusion
 * call without enough context.
 */
function FusionStep({
  state,
  set,
  fusionConfigs,
  clientId,
  disabled,
}: {
  state: FormState;
  set: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
  fusionConfigs: { id: string; name: string }[];
  clientId: string;
  disabled: boolean;
}): JSX.Element {
  const fusionConfigId = state.fusionConfigId || null;

  const orgsQuery = useFusionOrgsQuery(clientId, fusionConfigId);
  // Fan-out fetches — one request per selected team. The narrow-scope
  // panels render every team's folders/scenarios, grouped under team
  // headers, so operators spanning multiple teams can see the whole
  // picker tree at once rather than guessing which team to set as
  // "primary" first.
  const foldersByTeam = useFusionScenarioFoldersByTeamsQuery(
    clientId,
    fusionConfigId,
    state.fusionTeamIds,
  );
  const scenariosByTeam = useFusionScenariosByTeamsQuery(
    clientId,
    fusionConfigId,
    state.fusionTeamIds,
  );

  const selectedOrg = useMemo<FusionOrg | undefined>(
    () => orgsQuery.data?.find((o) => String(o.id) === state.fusionOrgId),
    [orgsQuery.data, state.fusionOrgId],
  );
  const teamsForOrg = selectedOrg?.teams ?? [];
  const teamNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of teamsForOrg) m.set(String(t.id), t.name);
    return m;
  }, [teamsForOrg]);
  // Folders+scenarios are panels-only data; flatten for the autofill
  // lookup below.
  const allScenariosFlat = useMemo<FusionScenario[]>(() => {
    const out: FusionScenario[] = [];
    for (const g of scenariosByTeam) out.push(...g.scenarios);
    return out;
  }, [scenariosByTeam]);
  const anyFolderPending = foldersByTeam.some((g) => g.isPending);
  const anyScenarioPending = scenariosByTeam.some((g) => g.isPending);
  const firstFolderError = foldersByTeam.find((g) => g.error)?.error ?? null;
  const firstScenarioError = scenariosByTeam.find((g) => g.error)?.error ?? null;

  const handleConfigChange = (next: string): void => {
    // Switching connection invalidates every downstream pick; reset
    // them so the operator doesn't accidentally save mismatched ids.
    set('fusionConfigId', next);
    set('fusionOrgId', '');
    set('fusionOrgName', '');
    set('fusionTeamIds', []);
    set('fusionTeamName', '');
    set('fusionScenarioFolderIds', []);
    set('fusionScenarioIds', []);
    set('fusionScenarioName', '');
  };

  const handleOrgChange = (nextOrgId: string): void => {
    set('fusionOrgId', nextOrgId);
    const org = orgsQuery.data?.find((o) => String(o.id) === nextOrgId);
    set('fusionOrgName', org?.name ?? '');
    set('fusionTeamIds', []);
    set('fusionTeamName', '');
    set('fusionScenarioFolderIds', []);
    set('fusionScenarioIds', []);
    set('fusionScenarioName', '');
  };

  const toggleTeam = (teamId: string, checked: boolean): void => {
    const next = checked
      ? [...new Set([...state.fusionTeamIds, teamId])]
      : state.fusionTeamIds.filter((id) => id !== teamId);
    set('fusionTeamIds', next);
    // Auto-fill team name from the first selected team (engine compat).
    const firstId = next[0];
    const team = firstId ? teamsForOrg.find((t) => String(t.id) === firstId) : undefined;
    set('fusionTeamName', team?.name ?? '');
    // Changing teams may invalidate the scenario picker context (the
    // folder + scenario sets are scoped to `primaryTeamId`). Keep the
    // current selection — operators may legitimately want to keep
    // ids they already picked when adding more teams — but the user
    // should re-check the dropdowns. Cleaner UX is "no clear on team
    // change, but stale ids may show as unchecked".
  };

  const toggleScenarioFolder = (folderId: string, checked: boolean): void => {
    const next = checked
      ? [...new Set([...state.fusionScenarioFolderIds, folderId])]
      : state.fusionScenarioFolderIds.filter((id) => id !== folderId);
    set('fusionScenarioFolderIds', next);
  };

  const toggleScenario = (scenarioId: string, checked: boolean): void => {
    const next = checked
      ? [...new Set([...state.fusionScenarioIds, scenarioId])]
      : state.fusionScenarioIds.filter((id) => id !== scenarioId);
    set('fusionScenarioIds', next);
    const firstId = next[0];
    const scenario = firstId ? allScenariosFlat.find((s) => String(s.id) === firstId) : undefined;
    set('fusionScenarioName', scenario?.name ?? '');
  };

  const handleNarrowModeChange = (mode: NarrowMode): void => {
    set('narrowMode', mode);
    // Mutual exclusion — flipping the radio clears the array that no
    // longer applies so we never persist a half-mode license. Re-picking
    // a mode an operator was just in starts from a fresh empty list,
    // which is the right safe default (operators may have edited the
    // teams in the meantime, invalidating any prior folder/scenario id
    // they had checked).
    if (mode !== 'folder') {
      set('fusionScenarioFolderIds', []);
    }
    if (mode !== 'scenario') {
      set('fusionScenarioIds', []);
      set('fusionScenarioName', '');
    }
  };

  return (
    <div className="mt-8 space-y-5">
      <div>
        <Label htmlFor="lic-fu-config">Fusion connection</Label>
        <Select
          id="lic-fu-config"
          value={state.fusionConfigId}
          onChange={(e) => handleConfigChange(e.target.value)}
          disabled={disabled}
        >
          <option value="">— None —</option>
          {fusionConfigs.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <HelperText className="mt-1">
          Pick a connection to populate the org / team / scenario dropdowns from the customer&apos;s
          Fusion tenant.
        </HelperText>
      </div>

      <div>
        <Label htmlFor="lic-fu-org">Organization</Label>
        <Select
          id="lic-fu-org"
          value={state.fusionOrgId}
          onChange={(e) => handleOrgChange(e.target.value)}
          disabled={disabled || !fusionConfigId || orgsQuery.isPending}
        >
          <option value="">
            {!fusionConfigId
              ? '— Pick a Fusion connection first —'
              : orgsQuery.isPending
                ? 'Loading orgs…'
                : '— Pick an org —'}
          </option>
          {orgsQuery.data?.map((o) => (
            <option key={o.id} value={String(o.id)}>
              {o.name} (#{o.id})
            </option>
          ))}
        </Select>
        {orgsQuery.isError ? <PickerError error={orgsQuery.error} /> : null}
      </div>

      <CheckboxPanel
        label="Teams"
        helper="Multi-select. Engine fans out fusion-metrics + workfront-object-detection across every checked team."
        emptyMessage={
          !state.fusionOrgId
            ? 'Pick an organization to list its teams.'
            : 'This organization has no teams.'
        }
        loading={false}
        error={null}
        items={teamsForOrg.map((t) => ({ id: String(t.id), label: `${t.name} (#${t.id})` }))}
        selected={state.fusionTeamIds}
        onToggle={toggleTeam}
        disabled={disabled || !state.fusionOrgId}
      />

      {/*
       * Scenario-scope is a tri-state radio. `None` (default) lets the
       * engine sync every scenario under the selected teams. Operators
       * can narrow by folder (sync every scenario inside the chosen
       * folder[s]) or by scenario (sync only the explicitly checked
       * scenarios). The modes are mutually exclusive — flipping the
       * radio clears whichever array no longer applies, so we never
       * persist a half-mode license.
       *
       * Hiding the non-active panel rather than disabling it keeps the
       * form compact for the 95% of licenses that pick `None`, and
       * makes the chosen mode obvious at a glance on edit.
       */}
      <fieldset className="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
        <legend className="px-1 text-sm font-medium">Sync scope</legend>
        <div className="space-y-2">
          {(
            [
              {
                value: 'none',
                title: 'All scenarios in the selected teams',
                helper:
                  'Default. Engine syncs every scenario under each picked team — no extra picking required.',
              },
              {
                value: 'folder',
                title: 'Specific scenario folders',
                helper:
                  'Engine syncs every scenario inside the picked folders. Useful when ops groups its production scenarios into a folder and wants to keep ad-hoc / experimental folders out.',
              },
              {
                value: 'scenario',
                title: 'Specific scenarios',
                helper:
                  'Engine syncs only the explicitly checked scenarios. Most precise; rebuild the list whenever ops adds a new scenario you care about.',
              },
            ] as const
          ).map((opt) => {
            const id = `lic-fu-narrow-mode-${opt.value}`;
            return (
              <div key={opt.value} className="flex items-start gap-2">
                <Radio
                  id={id}
                  name="lic-fu-narrow-mode"
                  value={opt.value}
                  checked={state.narrowMode === opt.value}
                  onChange={() => handleNarrowModeChange(opt.value)}
                  disabled={disabled || state.fusionTeamIds.length === 0}
                />
                <div className="flex-1">
                  <Label htmlFor={id} className="font-medium">
                    {opt.title}
                  </Label>
                  <HelperText className="mt-0.5">{opt.helper}</HelperText>
                </div>
              </div>
            );
          })}
          {state.fusionTeamIds.length === 0 ? (
            <HelperText className="italic">Pick at least one team first.</HelperText>
          ) : null}
        </div>
      </fieldset>

      {state.narrowMode === 'folder' ? (
        <TeamGroupedFolderPanel
          foldersByTeam={foldersByTeam}
          teamNameById={teamNameById}
          loading={anyFolderPending}
          error={firstFolderError}
          selected={state.fusionScenarioFolderIds}
          onToggle={toggleScenarioFolder}
          disabled={disabled || state.fusionTeamIds.length === 0}
        />
      ) : null}

      {state.narrowMode === 'scenario' ? (
        <TeamGroupedScenarioPanel
          scenariosByTeam={scenariosByTeam}
          foldersByTeam={foldersByTeam}
          teamNameById={teamNameById}
          loading={anyScenarioPending || anyFolderPending}
          error={firstScenarioError ?? firstFolderError}
          selected={state.fusionScenarioIds}
          onToggle={toggleScenario}
          disabled={disabled || state.fusionTeamIds.length === 0}
        />
      ) : null}
    </div>
  );
}

/**
 * Bordered checkbox-grid panel used for the multi-selects in the
 * Fusion step. Replaces the native `<select multiple>` (ugly browser
 * default) and a third-party multi-select dep (extra surface). Pure
 * Flowbite Checkboxes + Tailwind layout, scrolls past ~8 rows so
 * tenants with hundreds of scenarios stay usable.
 */
function CheckboxPanel({
  label,
  helper,
  emptyMessage,
  loading,
  error,
  items,
  selected,
  onToggle,
  disabled,
}: {
  label: string;
  helper?: string;
  emptyMessage: string;
  loading: boolean;
  error: Error | null;
  items: { id: string; label: string }[];
  selected: string[];
  onToggle: (id: string, checked: boolean) => void;
  disabled: boolean;
}): JSX.Element {
  const selectedSet = new Set(selected);
  return (
    <div>
      <Label>
        {label}
        {selected.length > 0 ? (
          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
            ({selected.length} selected)
          </span>
        ) : null}
      </Label>
      <div
        className={`mt-1 max-h-56 overflow-y-auto rounded-md border border-gray-300 bg-white p-2 dark:border-gray-600 dark:bg-gray-800 ${
          disabled ? 'opacity-60' : ''
        }`}
      >
        {loading ? (
          <div className="flex items-center gap-2 px-2 py-2 text-xs text-gray-500 dark:text-gray-400">
            <Spinner size="sm" />
            Loading…
          </div>
        ) : error ? (
          <PickerError error={error} />
        ) : items.length === 0 ? (
          <p className="px-2 py-2 text-xs italic text-gray-500 dark:text-gray-400">
            {emptyMessage}
          </p>
        ) : (
          <ul className="space-y-1">
            {items.map((item) => {
              const id = `lic-cb-${label}-${item.id}`.replace(/\s+/g, '-').toLowerCase();
              return (
                <li key={item.id} className="flex items-center gap-2 px-2 py-1">
                  <Checkbox
                    id={id}
                    checked={selectedSet.has(item.id)}
                    onChange={(e) => onToggle(item.id, e.target.checked)}
                    disabled={disabled}
                  />
                  <Label htmlFor={id} className="font-normal">
                    {item.label}
                  </Label>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {helper ? <HelperText className="mt-1">{helper}</HelperText> : null}
    </div>
  );
}

/**
 * Folder panel grouped by team. Each picked team gets a section header
 * with its folders as checkboxes underneath. Teams render in the
 * order the operator picked them on the Teams panel (matches the
 * `state.fusionTeamIds` array). Sections with zero folders show an
 * inline empty placeholder so operators see "this team has no
 * folders" rather than wondering if the fetch failed.
 */
function TeamGroupedFolderPanel({
  foldersByTeam,
  teamNameById,
  loading,
  error,
  selected,
  onToggle,
  disabled,
}: {
  foldersByTeam: FoldersByTeam[];
  teamNameById: Map<string, string>;
  loading: boolean;
  error: Error | null;
  selected: string[];
  onToggle: (id: string, checked: boolean) => void;
  disabled: boolean;
}): JSX.Element {
  const selectedSet = new Set(selected);
  const totalFolders = foldersByTeam.reduce((n, g) => n + g.folders.length, 0);
  return (
    <div>
      <Label>
        Scenario folders
        {selected.length > 0 ? (
          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
            ({selected.length} selected)
          </span>
        ) : null}
      </Label>
      <div
        className={`mt-1 max-h-72 overflow-y-auto rounded-md border border-gray-300 bg-white p-2 dark:border-gray-600 dark:bg-gray-800 ${
          disabled ? 'opacity-60' : ''
        }`}
      >
        {foldersByTeam.length === 0 ? (
          <p className="px-2 py-2 text-xs italic text-gray-500 dark:text-gray-400">
            Pick at least one team to list its scenario folders.
          </p>
        ) : loading && totalFolders === 0 ? (
          <div className="flex items-center gap-2 px-2 py-2 text-xs text-gray-500 dark:text-gray-400">
            <Spinner size="sm" />
            Loading…
          </div>
        ) : error && totalFolders === 0 ? (
          <PickerError error={error} />
        ) : (
          <div className="space-y-3">
            {foldersByTeam.map((g) => (
              <TeamFolderGroup
                key={g.teamId}
                heading={`${teamNameById.get(g.teamId) ?? 'Team'} (#${g.teamId})`}
                folders={g.folders}
                loading={g.isPending}
                error={g.error}
                selectedSet={selectedSet}
                onToggle={onToggle}
                disabled={disabled}
              />
            ))}
          </div>
        )}
      </div>
      <HelperText className="mt-1">
        Multi-select. Folders are grouped under each picked team.
      </HelperText>
    </div>
  );
}

function TeamFolderGroup({
  heading,
  folders,
  loading,
  error,
  selectedSet,
  onToggle,
  disabled,
}: {
  heading: string;
  folders: FusionScenarioFolder[];
  loading: boolean;
  error: Error | null;
  selectedSet: Set<string>;
  onToggle: (id: string, checked: boolean) => void;
  disabled: boolean;
}): JSX.Element {
  return (
    <div>
      <div className="border-b border-gray-200 px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:border-gray-700 dark:text-gray-300">
        {heading}
      </div>
      {loading ? (
        <div className="flex items-center gap-2 px-2 py-2 text-xs text-gray-500 dark:text-gray-400">
          <Spinner size="sm" />
          Loading…
        </div>
      ) : error ? (
        <PickerError error={error} />
      ) : folders.length === 0 ? (
        <p className="px-2 py-2 text-xs italic text-gray-500 dark:text-gray-400">
          No scenario folders in this team.
        </p>
      ) : (
        <ul className="mt-1 space-y-1">
          {folders.map((f) => {
            const fid = String(f.id);
            const id = `lic-cb-folder-${fid}`;
            return (
              <li key={fid} className="flex items-center gap-2 px-2 py-1">
                <Checkbox
                  id={id}
                  checked={selectedSet.has(fid)}
                  onChange={(e) => onToggle(fid, e.target.checked)}
                  disabled={disabled}
                />
                <Label htmlFor={id} className="font-normal">
                  {f.name} (#{f.id})
                </Label>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * Scenario panel grouped by team → folder. Each picked team gets a
 * top-level section header, with its scenarios grouped under their
 * parent folder name underneath. Scenarios without a parent folder
 * within a team land in a trailing "Ungrouped (no folder)" subsection
 * for that team (Fusion lets scenarios live at the team root). Two
 * levels of grouping keep the list legible for tenants with hundreds
 * of scenarios distributed across multiple teams.
 *
 * Folder names come from the matching team's folders fetch; if a
 * scenario points to a folder id we don't have a name for (race
 * between fetches, or the folder was just deleted), the section
 * header falls back to `Folder #<id>` so the operator still sees the
 * grouping rather than silently moving the scenario to "Ungrouped".
 */
function TeamGroupedScenarioPanel({
  scenariosByTeam,
  foldersByTeam,
  teamNameById,
  loading,
  error,
  selected,
  onToggle,
  disabled,
}: {
  scenariosByTeam: ScenariosByTeam[];
  foldersByTeam: FoldersByTeam[];
  teamNameById: Map<string, string>;
  loading: boolean;
  error: Error | null;
  selected: string[];
  onToggle: (id: string, checked: boolean) => void;
  disabled: boolean;
}): JSX.Element {
  const selectedSet = new Set(selected);
  // Look up the per-team folders payload by teamId so the nested
  // grouping inside each team can resolve folder names.
  const foldersByTeamId = new Map<string, FusionScenarioFolder[]>(
    foldersByTeam.map((g) => [g.teamId, g.folders]),
  );
  const totalScenarios = scenariosByTeam.reduce((n, g) => n + g.scenarios.length, 0);
  return (
    <div>
      <Label>
        Scenarios
        {selected.length > 0 ? (
          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
            ({selected.length} selected)
          </span>
        ) : null}
      </Label>
      <div
        className={`mt-1 max-h-[28rem] overflow-y-auto rounded-md border border-gray-300 bg-white p-2 dark:border-gray-600 dark:bg-gray-800 ${
          disabled ? 'opacity-60' : ''
        }`}
      >
        {scenariosByTeam.length === 0 ? (
          <p className="px-2 py-2 text-xs italic text-gray-500 dark:text-gray-400">
            Pick at least one team to list its scenarios.
          </p>
        ) : loading && totalScenarios === 0 ? (
          <div className="flex items-center gap-2 px-2 py-2 text-xs text-gray-500 dark:text-gray-400">
            <Spinner size="sm" />
            Loading…
          </div>
        ) : error && totalScenarios === 0 ? (
          <PickerError error={error} />
        ) : (
          <div className="space-y-4">
            {scenariosByTeam.map((g) => (
              <TeamScenarioGroup
                key={g.teamId}
                teamHeading={`${teamNameById.get(g.teamId) ?? 'Team'} (#${g.teamId})`}
                scenarios={g.scenarios}
                folders={foldersByTeamId.get(g.teamId) ?? []}
                loading={g.isPending}
                error={g.error}
                selectedSet={selectedSet}
                onToggle={onToggle}
                disabled={disabled}
              />
            ))}
          </div>
        )}
      </div>
      <HelperText className="mt-1">
        Multi-select. Scenarios are grouped first by team, then by their parent folder; folder
        sections sort by name with &quot;Ungrouped&quot; pinned to the end of each team.
      </HelperText>
    </div>
  );
}

function TeamScenarioGroup({
  teamHeading,
  scenarios,
  folders,
  loading,
  error,
  selectedSet,
  onToggle,
  disabled,
}: {
  teamHeading: string;
  scenarios: FusionScenario[];
  folders: FusionScenarioFolder[];
  loading: boolean;
  error: Error | null;
  selectedSet: Set<string>;
  onToggle: (id: string, checked: boolean) => void;
  disabled: boolean;
}): JSX.Element {
  // Folder lookup is per-team so the BIGINT folder ids (which Fusion
  // does NOT guarantee are unique across teams — they're scoped to a
  // team) match the right name even if two teams happen to have a
  // folder id collision.
  const folderNameById = new Map<string, string>(folders.map((f) => [String(f.id), f.name]));
  const groups = new Map<string | null, FusionScenario[]>();
  for (const s of scenarios) {
    const key = s.folderId ?? null;
    const bucket = groups.get(key) ?? [];
    bucket.push(s);
    groups.set(key, bucket);
  }
  const namedGroups = [...groups.entries()]
    .filter(([fid]) => fid !== null)
    .map(([fid, ss]) => ({
      fid: fid as string,
      name: folderNameById.get(fid as string),
      scenarios: ss,
    }));
  namedGroups.sort((a, b) => {
    if (a.name && b.name)
      return a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase());
    if (a.name) return -1;
    if (b.name) return 1;
    return a.fid.localeCompare(b.fid);
  });
  const ungrouped = groups.get(null) ?? [];
  return (
    <div className="rounded border border-gray-100 bg-gray-50/60 p-2 dark:border-gray-700 dark:bg-gray-900/30">
      <div className="border-b border-gray-200 px-1 pb-1 text-sm font-semibold text-gray-700 dark:border-gray-700 dark:text-gray-200">
        {teamHeading}
      </div>
      {loading ? (
        <div className="flex items-center gap-2 px-2 py-2 text-xs text-gray-500 dark:text-gray-400">
          <Spinner size="sm" />
          Loading…
        </div>
      ) : error ? (
        <PickerError error={error} />
      ) : scenarios.length === 0 ? (
        <p className="px-2 py-2 text-xs italic text-gray-500 dark:text-gray-400">
          No scenarios in this team.
        </p>
      ) : (
        <div className="mt-2 space-y-2 pl-2">
          {namedGroups.map((g) => (
            <ScenarioGroup
              key={g.fid}
              heading={g.name ? `${g.name} (#${g.fid})` : `Folder #${g.fid}`}
              scenarios={g.scenarios}
              selectedSet={selectedSet}
              onToggle={onToggle}
              disabled={disabled}
            />
          ))}
          {ungrouped.length > 0 ? (
            <ScenarioGroup
              key="__ungrouped"
              heading="Ungrouped (no folder)"
              scenarios={ungrouped}
              selectedSet={selectedSet}
              onToggle={onToggle}
              disabled={disabled}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

function ScenarioGroup({
  heading,
  scenarios,
  selectedSet,
  onToggle,
  disabled,
}: {
  heading: string;
  scenarios: FusionScenario[];
  selectedSet: Set<string>;
  onToggle: (id: string, checked: boolean) => void;
  disabled: boolean;
}): JSX.Element {
  return (
    <div>
      <div className="border-b border-gray-200 px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:border-gray-700 dark:text-gray-300">
        {heading}
      </div>
      <ul className="mt-1 space-y-1">
        {scenarios.map((s) => {
          const sid = String(s.id);
          const id = `lic-cb-scenario-${sid}`;
          return (
            <li key={sid} className="flex items-center gap-2 px-2 py-1">
              <Checkbox
                id={id}
                checked={selectedSet.has(sid)}
                onChange={(e) => onToggle(sid, e.target.checked)}
                disabled={disabled}
              />
              <Label htmlFor={id} className="font-normal">
                {s.name} (#{s.id})
              </Label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function PickerError({ error }: { error: Error | string }): JSX.Element {
  const message = typeof error === 'string' ? error : error.message;
  return (
    <div
      role="alert"
      className="mt-1 rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
    >
      {message}
    </div>
  );
}

/**
 * Cascading Workfront picker. Drives:
 *   1. Workfront connection dropdown (existing — picks
 *      workfront_configs rows for the tenant).
 *   2. Object detection connection dropdown (existing — separate
 *      connection used only by the workfront-object-detection
 *      pipeline; common case is "same as backup", indicated
 *      elsewhere via `workfrontReuseBackupConfig`).
 *   3. Portfolio dropdown — driven by the chosen primary connection
 *      via `useWorkfrontPortfoliosQuery`.
 *   4. Program dropdown — driven by the chosen portfolio via
 *      `useWorkfrontProgramsQuery`.
 *   5. Template dropdown (optional) — independent of portfolio,
 *      driven by the chosen connection via
 *      `useWorkfrontTemplatesQuery`. Templates are global in
 *      Workfront, not scoped to a portfolio.
 *
 * Required fields (zod): `workfrontPortfolioId`,
 * `workfrontProgramId`. Template is nullable. The picker only emits
 * Workfront-issued IDs, so the BIGINT-like ID strings the engine
 * stores are always well-formed.
 */
function WorkfrontStep({
  state,
  set,
  workfrontConfigs,
  clientId,
  disabled,
}: {
  state: FormState;
  set: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
  workfrontConfigs: { id: string; name: string }[];
  clientId: string;
  disabled: boolean;
}): JSX.Element {
  const workfrontConfigId = state.workfrontConfigId || null;
  const portfolioId = state.workfrontPortfolioId || null;

  const portfoliosQuery = useWorkfrontPortfoliosQuery(clientId, workfrontConfigId);
  const programsQuery = useWorkfrontProgramsQuery(clientId, workfrontConfigId, portfolioId);
  const templatesQuery = useWorkfrontTemplatesQuery(clientId, workfrontConfigId);

  const handleConfigChange = (next: string): void => {
    // Switching primary connection invalidates the portfolio /
    // program / template picks — different Workfront tenant, different
    // ID universe. Reset everything downstream.
    set('workfrontConfigId', next);
    set('workfrontPortfolioId', '');
    set('workfrontProgramId', '');
    set('workfrontTemplateId', '');
  };

  const handlePortfolioChange = (next: string): void => {
    set('workfrontPortfolioId', next);
    // Program belongs to a portfolio — reset when the parent changes.
    set('workfrontProgramId', '');
  };

  return (
    <div className="mt-8 space-y-5">
      <div>
        <Label htmlFor="lic-wf-config">Workfront connection</Label>
        <Select
          id="lic-wf-config"
          value={state.workfrontConfigId}
          onChange={(e) => handleConfigChange(e.target.value)}
          disabled={disabled}
        >
          <option value="">— None —</option>
          {workfrontConfigs.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <HelperText className="mt-1">
          Pick a connection to populate portfolio / program / template dropdowns from the
          customer&apos;s Workfront tenant.
        </HelperText>
      </div>

      <div>
        <Label htmlFor="lic-wf-od-config">Object detection connection</Label>
        <Select
          id="lic-wf-od-config"
          value={state.workfrontObjectDetectionConfigId}
          onChange={(e) => set('workfrontObjectDetectionConfigId', e.target.value)}
          disabled={disabled}
        >
          <option value="">— None —</option>
          {workfrontConfigs.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <HelperText className="mt-1">
          Optional. Separate connection used by the workfront-object-detection pipeline (read-only
          credentials in some tenants). Most clients leave this blank + flip{' '}
          <code className="font-mono text-xs">Workfront Reuse Backup Config</code> on the Flags
          step.
        </HelperText>
      </div>

      <SecondaryWorkfrontPicker
        workfrontConfigs={workfrontConfigs}
        primaryWorkfrontConfigId={state.workfrontConfigId}
        selectedIds={state.secondaryWorkfrontConfigIds}
        onChange={(ids) => set('secondaryWorkfrontConfigIds', ids)}
        disabled={disabled}
      />

      <div>
        <Label htmlFor="lic-wf-portfolio">
          Portfolio <span className="text-red-500">*</span>
        </Label>
        <Select
          id="lic-wf-portfolio"
          value={state.workfrontPortfolioId}
          onChange={(e) => handlePortfolioChange(e.target.value)}
          disabled={disabled || !workfrontConfigId || portfoliosQuery.isPending}
        >
          <option value="">
            {!workfrontConfigId
              ? '— Pick a Workfront connection first —'
              : portfoliosQuery.isPending
                ? 'Loading portfolios…'
                : '— Pick a portfolio —'}
          </option>
          {portfoliosQuery.data?.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
        {portfoliosQuery.isError ? <PickerError error={portfoliosQuery.error} /> : null}
      </div>

      <div>
        <Label htmlFor="lic-wf-program">
          Program <span className="text-red-500">*</span>
        </Label>
        <Select
          id="lic-wf-program"
          value={state.workfrontProgramId}
          onChange={(e) => set('workfrontProgramId', e.target.value)}
          disabled={disabled || !portfolioId || programsQuery.isPending}
        >
          <option value="">
            {!portfolioId
              ? '— Pick a portfolio first —'
              : programsQuery.isPending
                ? 'Loading programs…'
                : '— Pick a program —'}
          </option>
          {programsQuery.data?.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
        {programsQuery.isError ? <PickerError error={programsQuery.error} /> : null}
      </div>

      <div>
        <Label htmlFor="lic-wf-template">Template</Label>
        <Select
          id="lic-wf-template"
          value={state.workfrontTemplateId}
          onChange={(e) => set('workfrontTemplateId', e.target.value)}
          disabled={disabled || !workfrontConfigId || templatesQuery.isPending}
        >
          <option value="">
            {!workfrontConfigId
              ? '— Pick a Workfront connection first —'
              : templatesQuery.isPending
                ? 'Loading templates…'
                : '— None —'}
          </option>
          {templatesQuery.data?.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </Select>
        <HelperText className="mt-1">
          Optional. Workfront templates are global per tenant — not scoped to a portfolio.
        </HelperText>
        {templatesQuery.isError ? <PickerError error={templatesQuery.error} /> : null}
      </div>
    </div>
  );
}

/**
 * Multi-select picker for `license_secondary_workfront_configs`.
 * Surfaces every workfront-config on this tenant minus whichever one
 * is already the primary (selecting it twice would be confusing and
 * we strip it on submit anyway). Replicates the legacy
 * `wf-adminconsole`'s "Secondary Workfront Connections" multi-select
 * from the License edit modal — needed by the customer-app's
 * custom-forms migration wizard (Clone Custom Form) to populate its
 * target-workspace dropdown.
 */
function SecondaryWorkfrontPicker({
  workfrontConfigs,
  primaryWorkfrontConfigId,
  selectedIds,
  onChange,
  disabled,
}: {
  workfrontConfigs: { id: string; name: string }[];
  primaryWorkfrontConfigId: string;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled: boolean;
}): JSX.Element {
  const eligible = workfrontConfigs.filter((c) => c.id !== primaryWorkfrontConfigId);
  const selected = new Set(selectedIds);
  const toggle = (id: string, checked: boolean): void => {
    const next = new Set(selected);
    if (checked) next.add(id);
    else next.delete(id);
    onChange(Array.from(next));
  };
  return (
    <div>
      <Label>Secondary Workfront connections</Label>
      <HelperText className="mt-1">
        Pick additional Workfront workspaces this license can target. Surfaces in the customer
        app&apos;s custom-forms migration wizard as &quot;secondary&quot; workspace options for
        cloning forms across instances. Engine routing keys are{' '}
        <code className="font-mono text-xs">secondary:&lt;index&gt;</code> ordered by connection
        name.
      </HelperText>
      {eligible.length === 0 ? (
        <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400">
          {workfrontConfigs.length === 0
            ? 'No Workfront connections exist on this tenant yet — add one in the Workfront connections tab first.'
            : 'No other Workfront connections to pick from — add another connection in the Workfront connections tab.'}
        </div>
      ) : (
        <div className="mt-2 space-y-1.5 rounded-md border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
          {eligible.map((c) => (
            <label key={c.id} className="flex cursor-pointer items-center gap-2 text-sm">
              <Checkbox
                id={`lic-sec-wf-${c.id}`}
                checked={selected.has(c.id)}
                onChange={(e) => toggle(c.id, e.target.checked)}
                disabled={disabled}
              />
              <span className="text-gray-900 dark:text-gray-100">{c.name}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function FlagsStep({
  state,
  set,
  disabled,
  metricOptions,
  metricsEmptyLabel,
}: {
  state: FormState;
  set: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
  disabled: boolean;
  metricOptions: ReadonlyArray<{ id: number; displayName: string }>;
  metricsEmptyLabel: string;
}): JSX.Element {
  return (
    <div className="mt-8 space-y-3">
      <ToggleRow
        id="lic-feat-ai"
        label="AI Summary"
        checked={state.aiSummaryEnabled}
        onChange={(c) => set('aiSummaryEnabled', c)}
        disabled={disabled}
      />
      <ToggleRow
        id="lic-feat-log"
        label="Log Metrics"
        checked={state.logMetricsEnabled}
        onChange={(c) => set('logMetricsEnabled', c)}
        disabled={disabled}
      />
      <ToggleRow
        id="lic-feat-wf"
        label="Workfront Backup Enabled"
        checked={state.workfrontBackupEnabled}
        onChange={(c) => set('workfrontBackupEnabled', c)}
        disabled={disabled}
      />
      <ToggleRow
        id="lic-feat-gh"
        label="GitHub Backup Enabled"
        checked={state.githubBackupEnabled}
        onChange={(c) => set('githubBackupEnabled', c)}
        disabled={disabled}
      />
      <ToggleRow
        id="lic-feat-reuse"
        label="Workfront Reuse Backup Config"
        checked={state.workfrontReuseBackupConfig}
        onChange={(c) => set('workfrontReuseBackupConfig', c)}
        disabled={disabled}
      />
      <div>
        <Label htmlFor="lic-main-metric">Main metric</Label>
        <Select
          id="lic-main-metric"
          value={state.mainMetricId}
          onChange={(e) => set('mainMetricId', e.target.value)}
          disabled={disabled || metricOptions.length === 0}
        >
          {metricOptions.length === 0 ? (
            <option value="">{metricsEmptyLabel}</option>
          ) : (
            <>
              <option value="">None</option>
              {metricOptions.map((m) => (
                <option key={m.id} value={String(m.id)}>
                  {m.displayName}
                </option>
              ))}
            </>
          )}
        </Select>
      </div>
    </div>
  );
}

function ToggleRow({
  id,
  label,
  checked,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (c: boolean) => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-4">
      <Label htmlFor={id} className="font-normal">
        {label}
      </Label>
      <ToggleSwitch
        id={id}
        color="green"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
    </div>
  );
}

function LicenseDeleteModal({
  open,
  onClose,
  clientId,
  license,
}: {
  open: boolean;
  onClose: () => void;
  clientId: string;
  license: LicenseDto;
}): JSX.Element {
  const del = useDeleteLicenseMutation();

  const handleConfirm = (): void => {
    del.mutate({ clientId, licenseId: license.id }, { onSuccess: onClose });
  };

  return (
    <Modal show={open} onClose={onClose} dismissible size="md">
      <ModalHeader>Delete license</ModalHeader>
      <ModalBody>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          This will permanently delete the{' '}
          <span className="font-semibold">{LICENSE_TYPE_LABEL[license.type] ?? license.type}</span>{' '}
          license. The Workfront / Fusion connection rows it referenced stay intact.
        </p>
        {del.isError ? (
          <div
            role="alert"
            className="mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
          >
            {del.error instanceof Error ? del.error.message : 'Failed to delete'}
          </div>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button color="gray" onClick={onClose} disabled={del.isPending}>
          Cancel
        </Button>
        <Button color="failure" onClick={handleConfirm} disabled={del.isPending}>
          {del.isPending ? 'Deleting…' : 'Delete'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function LicenseRotateConfirmModal({
  open,
  onClose,
  clientId,
  license,
  onRotated,
}: {
  open: boolean;
  onClose: () => void;
  clientId: string;
  license: LicenseDto;
  onRotated: (revealed: RevealedSecret) => void;
}): JSX.Element {
  const rotate = useRotateEngineCredentialsMutation();

  const handleConfirm = (): void => {
    rotate.mutate(
      { clientId, licenseId: license.id },
      {
        onSuccess: (data) => {
          onRotated({
            engineApiClientId: data.engineApiClientId,
            engineApiClientSecret: data.engineApiClientSecret,
            origin: 'rotated',
          });
        },
      },
    );
  };

  return (
    <Modal show={open} onClose={onClose} dismissible size="md">
      <ModalHeader>Rotate engine API credential</ModalHeader>
      <ModalBody>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Generate a new <code className="font-mono text-xs">engineApiClientSecret</code> for the{' '}
          <span className="font-semibold">{LICENSE_TYPE_LABEL[license.type] ?? license.type}</span>{' '}
          license. The current secret keeps working until the new one is shown — copy and paste it
          into <code className="font-mono text-xs">wf-audit</code>'s license-detail store
          immediately. Existing JWTs will continue to validate for their TTL window (15 min).
        </p>
        <p className="mt-3 text-sm text-amber-700 dark:text-amber-300">
          The new secret is shown <span className="font-semibold">only once</span>. If you lose it,
          rotate again.
        </p>
        {rotate.isError ? (
          <div
            role="alert"
            className="mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
          >
            {rotate.error instanceof Error ? rotate.error.message : 'Rotation failed'}
          </div>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button color="gray" onClick={onClose} disabled={rotate.isPending}>
          Cancel
        </Button>
        <Button color="warning" onClick={handleConfirm} disabled={rotate.isPending}>
          {rotate.isPending ? 'Rotating…' : 'Rotate credential'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function EngineApiSecretDisclosureModal({
  open,
  onClose,
  revealed,
}: {
  open: boolean;
  onClose: () => void;
  revealed: RevealedSecret;
}): JSX.Element {
  const [copiedField, setCopiedField] = useState<'id' | 'secret' | null>(null);

  const copy = async (value: string, which: 'id' | 'secret'): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(which);
      // Reset the "Copied" state after 2s so the operator can copy again.
      window.setTimeout(() => setCopiedField(null), 2000);
    } catch {
      // Clipboard API can fail on insecure contexts or older browsers;
      // operator can still select-and-copy manually. Don't crash.
    }
  };

  const title =
    revealed.origin === 'created'
      ? 'License created — engine API credential'
      : 'Engine API credential rotated';

  return (
    <Modal show={open} onClose={onClose} dismissible={false} size="lg">
      <ModalHeader>{title}</ModalHeader>
      <ModalBody>
        <div
          role="alert"
          className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200"
        >
          <p className="font-semibold">This is the only time the secret will be shown.</p>
          <p className="mt-1">
            Copy both values into <code className="font-mono text-xs">wf-audit</code>'s
            license-detail store. If you lose the secret, you'll have to rotate again.
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              engineApiClientId
            </Label>
            <div className="mt-1 flex items-center gap-2">
              <code
                className="flex-1 break-all rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                data-testid="reveal-engine-api-client-id"
              >
                {revealed.engineApiClientId}
              </code>
              <Button
                color="gray"
                size="xs"
                onClick={() => void copy(revealed.engineApiClientId, 'id')}
                aria-label="Copy engineApiClientId"
              >
                <FiCopy className="mr-1" />
                {copiedField === 'id' ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              engineApiClientSecret
            </Label>
            <div className="mt-1 flex items-center gap-2">
              <code
                className="flex-1 break-all rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                data-testid="reveal-engine-api-client-secret"
              >
                {revealed.engineApiClientSecret}
              </code>
              <Button
                color="gray"
                size="xs"
                onClick={() => void copy(revealed.engineApiClientSecret, 'secret')}
                aria-label="Copy engineApiClientSecret"
              >
                <FiCopy className="mr-1" />
                {copiedField === 'secret' ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="gray" onClick={onClose}>
          I have copied the secret
        </Button>
      </ModalFooter>
    </Modal>
  );
}
